/**
 * Color Converter
 * Per R-4: Focused class for color format conversions
 * Handles conversions between hex, RGB, and HSV color formats
 */
import { createHexColor, ErrorCode, AppError } from '@xivdyetools/types';
import { RGB_MIN, RGB_MAX, HUE_MAX, COLOR_DISTANCE_MAX } from '../../constants/index.js';
import { clamp, round, isValidHexColor, isValidRGB, isValidHSV, LRUCache, } from '../../utils/index.js';
/**
 * Fold the legacy `'cie2000'` spelling onto the canonical `'ciede2000'`.
 *
 * Both name the same CIEDE2000 math and always have; only the spelling ever
 * differed (`DeltaEFormula` said `'cie2000'`, `MatchingMethod` said
 * `'ciede2000'`). This is the single point where that is reconciled — call it
 * at any entry point that accepts a caller-supplied `DeltaEFormula`, never
 * re-derive the mapping with another switch.
 */
export function normalizeDeltaEFormula(formula) {
    return formula === 'cie2000' ? 'ciede2000' : formula;
}
/**
 * ΔEOK2's chroma scaling (CSS Color 4 §20.4). See `getDeltaE_Oklab`.
 */
const OKLAB_AB_SCALE = 2;
/**
 * The CIELAB companding constants, as the EXACT rationals of CIE 15:2004.
 *
 * This repo carried the pre-2004 decimal approximations (`0.008856` and
 * `903.3`) until 5.1.0. CIE 15:2004 replaced them with these fractions
 * specifically because rounding the two independently leaves the cube-root and
 * linear branches of f(t) **not meeting** at the junction: measured here, the
 * old pair left a step of 2.83e-7 in f (≈3.3e-5 in L*), which also makes f very
 * slightly non-monotonic and therefore non-invertible right at t = ε. With the
 * exact fractions the same measurement gives 2.78e-17 — continuous to floating
 * point.
 *
 * Visually this is nothing: swapping them moved no dye's nearest-neighbour
 * ranking (0 of 125) and changed ΔE00 by at most 7.6e-5. It is corrected
 * because it costs nothing and removes a real discontinuity, not because
 * anyone could see it.
 *
 * ⚠️ The D65 white point and sRGB matrix are deliberately NOT touched. They are
 * the ASTM/Lindbloom pair and are consistent WITH EACH OTHER, which is the
 * property that matters; CSS Color 4's alternative pair differs by ΔE00 ≈ 0.015
 * and swapping one without the other would be the actual bug.
 */
const CIE_EPSILON = 216 / 24389; // (6/29)^3 exactly
const CIE_KAPPA = 24389 / 27; // (29/3)^3 exactly
/**
 * Color format converter with LRU caching
 * Per R-4: Single Responsibility - format conversions only
 *
 * Refactored for testability: Supports dependency injection of cache configuration
 */
export class ColorConverter {
    // Instance caches (injectable for testing)
    hexToRgbCache;
    rgbToHexCache;
    rgbToHsvCache;
    hsvToRgbCache;
    hexToHsvCache;
    rgbToLabCache;
    rgbToOklabCache;
    // Default singleton instance for static API compatibility
    // Per Issue #6: Eager initialization to avoid race conditions in concurrent scenarios
    static defaultInstance = new ColorConverter();
    /**
     * Constructor with optional cache configuration
     * @param config Configuration for cache sizes (default: 1000 entries per cache)
     */
    constructor(config = {}) {
        const cacheSize = config.cacheSize ?? 1000;
        this.hexToRgbCache = new LRUCache(cacheSize);
        this.rgbToHexCache = new LRUCache(cacheSize);
        this.rgbToHsvCache = new LRUCache(cacheSize);
        this.hsvToRgbCache = new LRUCache(cacheSize);
        this.hexToHsvCache = new LRUCache(cacheSize);
        this.rgbToLabCache = new LRUCache(cacheSize);
        this.rgbToOklabCache = new LRUCache(cacheSize);
    }
    /**
     * Get the default singleton instance
     * Per Issue #6: Returns eagerly-initialized instance to avoid race conditions
     */
    static getDefault() {
        return this.defaultInstance;
    }
    /**
     * Clear all caches (useful for testing or memory management)
     */
    clearCaches() {
        this.hexToRgbCache.clear();
        this.rgbToHexCache.clear();
        this.rgbToHsvCache.clear();
        this.hsvToRgbCache.clear();
        this.hexToHsvCache.clear();
        this.rgbToLabCache.clear();
        this.rgbToOklabCache.clear();
    }
    /**
     * Static method: Clear all caches of the default instance
     */
    static clearCaches() {
        this.getDefault().clearCaches();
    }
    /**
     * Get cache statistics (for monitoring)
     */
    getCacheStats() {
        return {
            hexToRgb: this.hexToRgbCache.size,
            rgbToHex: this.rgbToHexCache.size,
            rgbToHsv: this.rgbToHsvCache.size,
            hsvToRgb: this.hsvToRgbCache.size,
            hexToHsv: this.hexToHsvCache.size,
            rgbToLab: this.rgbToLabCache.size,
            rgbToOklab: this.rgbToOklabCache.size,
        };
    }
    /**
     * Static method: Get cache statistics from default instance
     */
    static getCacheStats() {
        return this.getDefault().getCacheStats();
    }
    /**
     * Convert hexadecimal color to RGB
     * Per P-1: Cached for performance
     * @example hexToRgb("#FF0000") -> { r: 255, g: 0, b: 0 }
     */
    /**
     * REFACTOR-014 (2026-07-18 audit): uppercased, #-stripped, shorthand-expanded
     * hex — the single source of truth used as cache key AND parse source.
     */
    static normalizeHexKey(hex) {
        let h = hex.toUpperCase().replace('#', '');
        if (h.length === 3) {
            h = h
                .split('')
                .map((c) => c + c)
                .join('');
        }
        return h;
    }
    hexToRgb(hex) {
        if (!isValidHexColor(hex)) {
            throw new AppError(ErrorCode.INVALID_HEX_COLOR, `Invalid hex color: ${hex}. Use format #RRGGBB or #RGB`, 'error');
        }
        // REFACTOR-014 (2026-07-18 audit): one normalization pass serves as both
        // cache key and parse source (previously computed twice, asymmetrically)
        const cacheKey = ColorConverter.normalizeHexKey(hex);
        // Check cache
        const cached = this.hexToRgbCache.get(cacheKey);
        if (cached) {
            // BUG-005 (2026-07-18 audit): defensive copy — callers mutating the
            // returned object must not poison the process-wide cache
            return { ...cached };
        }
        const r = parseInt(cacheKey.substring(0, 2), 16);
        const g = parseInt(cacheKey.substring(2, 4), 16);
        const b = parseInt(cacheKey.substring(4, 6), 16);
        const result = { r, g, b };
        // Cache result
        this.hexToRgbCache.set(cacheKey, result);
        return { ...result };
    }
    /**
     * Static method: Convert hex to RGB using default instance
     */
    static hexToRgb(hex) {
        return this.getDefault().hexToRgb(hex);
    }
    /**
     * Convert RGB to hexadecimal color
     * Per P-1: Cached for performance
     * @example rgbToHex(255, 0, 0) -> "#FF0000"
     */
    rgbToHex(r, g, b) {
        if (!isValidRGB(r, g, b)) {
            throw new AppError(ErrorCode.INVALID_RGB_VALUE, `Invalid RGB values: r=${r}, g=${g}, b=${b}. Values must be 0-255`, 'error');
        }
        // Create cache key
        const cacheKey = `${r},${g},${b}`;
        // Check cache
        const cached = this.rgbToHexCache.get(cacheKey);
        if (cached) {
            return cached;
        }
        const toHex = (value) => {
            const hex = Math.round(value).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };
        const result = createHexColor(`#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase());
        // Cache result
        this.rgbToHexCache.set(cacheKey, result);
        return result;
    }
    /**
     * Static method: Convert RGB to hex using default instance
     */
    static rgbToHex(r, g, b) {
        return this.getDefault().rgbToHex(r, g, b);
    }
    /**
     * Convert RGB to HSV
     * Per P-1: Cached for performance, optimized single-pass min/max
     * @example rgbToHsv(255, 0, 0) -> { h: 0, s: 100, v: 100 }
     */
    rgbToHsv(r, g, b) {
        if (!isValidRGB(r, g, b)) {
            throw new AppError(ErrorCode.INVALID_RGB_VALUE, `Invalid RGB values: r=${r}, g=${g}, b=${b}`, 'error');
        }
        // Create cache key
        const cacheKey = `${r},${g},${b}`;
        // Check cache
        const cached = this.rgbToHsvCache.get(cacheKey);
        if (cached) {
            // BUG-005 (2026-07-18 audit): defensive copy — callers mutating the
            // returned object must not poison the process-wide cache
            return { ...cached };
        }
        // Per P-1: Optimized single-pass min/max calculation
        // Normalize RGB to 0-1 range
        const rNorm = r / 255;
        const gNorm = g / 255;
        const bNorm = b / 255;
        // Single-pass min/max (optimized)
        const max = Math.max(rNorm, gNorm, bNorm);
        const min = Math.min(rNorm, gNorm, bNorm);
        const delta = max - min;
        // Calculate Value (brightness)
        const v = max * 100;
        // Calculate Saturation
        const s = max === 0 ? 0 : (delta / max) * 100;
        // Calculate Hue
        let h = 0;
        if (delta !== 0) {
            if (max === rNorm) {
                h = ((gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0)) * 60;
            }
            else if (max === gNorm) {
                h = ((bNorm - rNorm) / delta + 2) * 60;
            }
            else {
                h = ((rNorm - gNorm) / delta + 4) * 60;
            }
        }
        const result = { h: round(h, 2), s: round(s, 2), v: round(v, 2) };
        // Cache result
        this.rgbToHsvCache.set(cacheKey, result);
        return { ...result };
    }
    /**
     * Static method: Convert RGB to HSV using default instance
     */
    static rgbToHsv(r, g, b) {
        return this.getDefault().rgbToHsv(r, g, b);
    }
    /**
     * Normalize hue to [0, 360) range.
     * CORE-BUG-001: Ensures consistent cache keys for equivalent hue values
     * (e.g., h=360 and h=0 share one key). Also handles negative values and
     * values >= 360.
     */
    normalizeHue(h) {
        return ((h % HUE_MAX) + HUE_MAX) % HUE_MAX;
    }
    /**
     * Convert HSV to RGB
     * Per P-1: Cached for performance
     * @example hsvToRgb(0, 100, 100) -> { r: 255, g: 0, b: 0 }
     */
    hsvToRgb(h, s, v) {
        if (!isValidHSV(h, s, v)) {
            throw new AppError(ErrorCode.INVALID_RGB_VALUE, `Invalid HSV values: h=${h}, s=${s}, v=${v}`, 'error');
        }
        // CORE-BUG-001: Normalize hue BEFORE creating cache key, so h=360 shares
        // the entry of h=0 (isValidHSV admits the closed range [0, 360]).
        const hNormalized = this.normalizeHue(h);
        // BUG-135 (2026-10-04 deep-dive): key on the EXACT inputs the result is
        // computed from. The key used to be rounded to 2 dp while the value was
        // computed from the unrounded inputs, so two inputs sharing a rounded
        // key (s=50.004 → g=127, s=49.996 → g=128) got whichever answer was
        // cached first — the output depended on call order. Exact keys leave
        // every fresh-cache result unchanged; continuous inputs (gradient
        // interpolation) were near-unique per step under rounding anyway.
        const cacheKey = `${hNormalized},${s},${v}`;
        // Check cache
        const cached = this.hsvToRgbCache.get(cacheKey);
        if (cached) {
            // BUG-005 (2026-07-18 audit): defensive copy — callers mutating the
            // returned object must not poison the process-wide cache
            return { ...cached };
        }
        // Normalize HSV (use already normalized hue)
        const hNorm = hNormalized / 60;
        const sNorm = s / 100;
        const vNorm = v / 100;
        const c = vNorm * sNorm;
        const x = c * (1 - Math.abs((hNorm % 2) - 1));
        const m = vNorm - c;
        let rNorm, gNorm, bNorm;
        if (hNorm >= 0 && hNorm < 1) {
            [rNorm, gNorm, bNorm] = [c, x, 0];
        }
        else if (hNorm >= 1 && hNorm < 2) {
            [rNorm, gNorm, bNorm] = [x, c, 0];
        }
        else if (hNorm >= 2 && hNorm < 3) {
            [rNorm, gNorm, bNorm] = [0, c, x];
        }
        else if (hNorm >= 3 && hNorm < 4) {
            [rNorm, gNorm, bNorm] = [0, x, c];
        }
        else if (hNorm >= 4 && hNorm < 5) {
            [rNorm, gNorm, bNorm] = [x, 0, c];
        }
        else {
            [rNorm, gNorm, bNorm] = [c, 0, x];
        }
        const r = Math.round((rNorm + m) * 255);
        const g = Math.round((gNorm + m) * 255);
        const b = Math.round((bNorm + m) * 255);
        const result = {
            r: clamp(r, RGB_MIN, RGB_MAX),
            g: clamp(g, RGB_MIN, RGB_MAX),
            b: clamp(b, RGB_MIN, RGB_MAX),
        };
        // Cache result
        this.hsvToRgbCache.set(cacheKey, result);
        return { ...result };
    }
    /**
     * Static method: Convert HSV to RGB using default instance
     */
    static hsvToRgb(h, s, v) {
        return this.getDefault().hsvToRgb(h, s, v);
    }
    /**
     * Convert hex to HSV
     * Per P-1: Cached for performance
     */
    hexToHsv(hex) {
        // BUG-009 (2026-09-16 audit): validate BEFORE the cache lookup below, or a
        // bare (unprefixed) hex that happens to already be warm in the cache
        // (from a prior `#`-prefixed call) returns successfully while the same
        // string cold throws — a consistency defect, not a value defect.
        if (!isValidHexColor(hex)) {
            throw new AppError(ErrorCode.INVALID_HEX_COLOR, `Invalid hex color: ${hex}. Use format #RRGGBB or #RGB`, 'error');
        }
        // REFACTOR-014: shared normalization (cache keys can't drift from parsing)
        const cacheKey = ColorConverter.normalizeHexKey(hex);
        // Check cache
        const cached = this.hexToHsvCache.get(cacheKey);
        if (cached) {
            // BUG-005 (2026-07-18 audit): defensive copy — callers mutating the
            // returned object must not poison the process-wide cache
            return { ...cached };
        }
        const rgb = this.hexToRgb(hex);
        const result = this.rgbToHsv(rgb.r, rgb.g, rgb.b);
        // Cache result
        this.hexToHsvCache.set(cacheKey, result);
        return { ...result };
    }
    /**
     * Static method: Convert hex to HSV using default instance
     */
    static hexToHsv(hex) {
        return this.getDefault().hexToHsv(hex);
    }
    /**
     * Convert HSV to hex
     */
    hsvToHex(h, s, v) {
        const rgb = this.hsvToRgb(h, s, v);
        return this.rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert HSV to hex using default instance
     */
    static hsvToHex(h, s, v) {
        return this.getDefault().hsvToHex(h, s, v);
    }
    /**
     * Normalize a hex color to #RRGGBB format
     */
    normalizeHex(hex) {
        const rgb = this.hexToRgb(hex);
        return this.rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Normalize hex using default instance
     */
    static normalizeHex(hex) {
        return this.getDefault().normalizeHex(hex);
    }
    /**
     * Calculate Euclidean distance between two RGB colors
     * Returns 0 for identical colors, ~441.67 for white vs black
     */
    getColorDistance(hex1, hex2) {
        const rgb1 = this.hexToRgb(hex1);
        const rgb2 = this.hexToRgb(hex2);
        const dr = rgb1.r - rgb2.r;
        const dg = rgb1.g - rgb2.g;
        const db = rgb1.b - rgb2.b;
        return Math.sqrt(dr * dr + dg * dg + db * db);
    }
    /**
     * Static method: Calculate color distance using default instance
     */
    static getColorDistance(hex1, hex2) {
        return this.getDefault().getColorDistance(hex1, hex2);
    }
    /**
     * Calculate "redmean" weighted RGB distance between two colors.
     *
     * A low-cost approximation of perceptual distance that weights the RGB
     * channels by the mean red level (https://en.wikipedia.org/wiki/Color_difference#sRGB):
     *   r̄ = (R1 + R2) / 2
     *   ΔC = √((2 + r̄/256)·ΔR² + 4·ΔG² + (2 + (255 − r̄)/256)·ΔB²)
     *
     * Range: 0 for identical colors, ~765 for white vs black.
     */
    getRedmeanDistance(hex1, hex2) {
        const rgb1 = this.hexToRgb(hex1);
        const rgb2 = this.hexToRgb(hex2);
        const rMean = (rgb1.r + rgb2.r) / 2;
        const dr = rgb1.r - rgb2.r;
        const dg = rgb1.g - rgb2.g;
        const db = rgb1.b - rgb2.b;
        return Math.sqrt((2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db);
    }
    /**
     * Static method: Calculate redmean distance using default instance
     */
    static getRedmeanDistance(hex1, hex2) {
        return this.getDefault().getRedmeanDistance(hex1, hex2);
    }
    /**
     * Distinguishability percentage between two colors.
     *
     * This is RGB Euclidean distance rescaled to 0-100:
     * `round(distance / 441.67 × 100)`. It is NOT a separate metric — ranks are
     * always identical to RGB distance, and integer rounding creates ties, so any
     * ordering driven by this value needs a sort fallback (and ties should be
     * badged as ties, never presented as a single answer). Kept for continuity
     * with the Accessibility readout. Not a WCAG standard.
     */
    getDistinguishabilityPercent(hex1, hex2) {
        return Math.round((this.getColorDistance(hex1, hex2) / COLOR_DISTANCE_MAX) * 100);
    }
    /**
     * Static method: Distinguishability percentage using default instance
     */
    static getDistinguishabilityPercent(hex1, hex2) {
        return this.getDefault().getDistinguishabilityPercent(hex1, hex2);
    }
    // ============================================================================
    // LAB Color Space Conversion
    // ============================================================================
    /**
     * Convert RGB to CIE XYZ color space (intermediate step for LAB)
     * Uses sRGB to XYZ conversion with D65 illuminant
     * @internal
     */
    rgbToXyz(r, g, b) {
        // Normalize to 0-1 and apply sRGB gamma expansion
        let rLinear = r / 255;
        let gLinear = g / 255;
        let bLinear = b / 255;
        // sRGB gamma expansion (inverse companding)
        rLinear = rLinear > 0.04045 ? Math.pow((rLinear + 0.055) / 1.055, 2.4) : rLinear / 12.92;
        gLinear = gLinear > 0.04045 ? Math.pow((gLinear + 0.055) / 1.055, 2.4) : gLinear / 12.92;
        bLinear = bLinear > 0.04045 ? Math.pow((bLinear + 0.055) / 1.055, 2.4) : bLinear / 12.92;
        // sRGB to XYZ matrix (D65 illuminant)
        return {
            x: rLinear * 0.4124564 + gLinear * 0.3575761 + bLinear * 0.1804375,
            y: rLinear * 0.2126729 + gLinear * 0.7151522 + bLinear * 0.072175,
            z: rLinear * 0.0193339 + gLinear * 0.119192 + bLinear * 0.9503041,
        };
    }
    /**
     * Convert RGB to CIE LAB color space
     * Per P-1: Cached for performance
     * @example rgbToLab(255, 0, 0) -> { L: 53.23, a: 80.11, b: 67.22 }
     */
    rgbToLab(r, g, b) {
        if (!isValidRGB(r, g, b)) {
            throw new AppError(ErrorCode.INVALID_RGB_VALUE, `Invalid RGB values: r=${r}, g=${g}, b=${b}. Values must be 0-255`, 'error');
        }
        const cacheKey = `${r},${g},${b}`;
        const cached = this.rgbToLabCache.get(cacheKey);
        if (cached) {
            // BUG-005 (2026-07-18 audit): defensive copy — callers mutating the
            // returned object must not poison the process-wide cache
            return { ...cached };
        }
        // D65 reference white point
        const refX = 0.95047;
        const refY = 1.0;
        const refZ = 1.08883;
        const xyz = this.rgbToXyz(r, g, b);
        // Normalize by reference white
        let x = xyz.x / refX;
        let y = xyz.y / refY;
        let z = xyz.z / refZ;
        // Apply LAB transformation (cube root with linear segment for dark colors)
        x = x > CIE_EPSILON ? Math.pow(x, 1 / 3) : (CIE_KAPPA * x + 16) / 116;
        y = y > CIE_EPSILON ? Math.pow(y, 1 / 3) : (CIE_KAPPA * y + 16) / 116;
        z = z > CIE_EPSILON ? Math.pow(z, 1 / 3) : (CIE_KAPPA * z + 16) / 116;
        // No rounding. The former `round(…, 4)` here cost ~1e-4 of precision in
        // every downstream ΔE for no benefit — LAB is an intermediate, not a
        // display value, and every caller that shows a number rounds it itself.
        const result = {
            L: 116 * y - 16,
            a: 500 * (x - y),
            b: 200 * (y - z),
        };
        this.rgbToLabCache.set(cacheKey, result);
        return { ...result };
    }
    /**
     * Static method: Convert RGB to LAB using default instance
     */
    static rgbToLab(r, g, b) {
        return this.getDefault().rgbToLab(r, g, b);
    }
    /**
     * Convert hex color to CIE LAB
     * @example hexToLab("#FF0000") -> { L: 53.23, a: 80.11, b: 67.22 }
     */
    hexToLab(hex) {
        const rgb = this.hexToRgb(hex);
        return this.rgbToLab(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert hex to LAB using default instance
     */
    static hexToLab(hex) {
        return this.getDefault().hexToLab(hex);
    }
    /**
     * Convert CIE XYZ to RGB
     * Uses sRGB matrix and gamma companding (D65 illuminant)
     * @internal
     */
    xyzToRgb(x, y, z) {
        // XYZ to sRGB matrix (D65 illuminant)
        let rLinear = x * 3.2404542 + y * -1.5371385 + z * -0.4985314;
        let gLinear = x * -0.969266 + y * 1.8760108 + z * 0.041556;
        let bLinear = x * 0.0556434 + y * -0.2040259 + z * 1.0572252;
        // sRGB gamma companding
        rLinear = rLinear > 0.0031308 ? 1.055 * Math.pow(rLinear, 1 / 2.4) - 0.055 : 12.92 * rLinear;
        gLinear = gLinear > 0.0031308 ? 1.055 * Math.pow(gLinear, 1 / 2.4) - 0.055 : 12.92 * gLinear;
        bLinear = bLinear > 0.0031308 ? 1.055 * Math.pow(bLinear, 1 / 2.4) - 0.055 : 12.92 * bLinear;
        return {
            r: clamp(Math.round(rLinear * 255), RGB_MIN, RGB_MAX),
            g: clamp(Math.round(gLinear * 255), RGB_MIN, RGB_MAX),
            b: clamp(Math.round(bLinear * 255), RGB_MIN, RGB_MAX),
        };
    }
    /**
     * Convert CIE LAB to RGB
     * @example labToRgb(53.23, 80.11, 67.22) -> { r: 255, g: 0, b: 0 }
     */
    labToRgb(L, a, b) {
        // D65 reference white point
        const refX = 0.95047;
        const refY = 1.0;
        const refZ = 1.08883;
        // LAB to XYZ
        let y = (L + 16) / 116;
        let x = a / 500 + y;
        let z = y - b / 200;
        // Reverse LAB transformation
        const x3 = x * x * x;
        const y3 = y * y * y;
        const z3 = z * z * z;
        x = x3 > CIE_EPSILON ? x3 : (116 * x - 16) / CIE_KAPPA;
        y = L > CIE_KAPPA * CIE_EPSILON ? y3 : L / CIE_KAPPA;
        z = z3 > CIE_EPSILON ? z3 : (116 * z - 16) / CIE_KAPPA;
        // Apply reference white
        return this.xyzToRgb(x * refX, y * refY, z * refZ);
    }
    /**
     * Static method: Convert LAB to RGB using default instance
     */
    static labToRgb(L, a, b) {
        return this.getDefault().labToRgb(L, a, b);
    }
    /**
     * Convert CIE LAB to hex color
     * @example labToHex(53.23, 80.11, 67.22) -> "#FF0000"
     */
    labToHex(L, a, b) {
        const rgb = this.labToRgb(L, a, b);
        return this.rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert LAB to hex using default instance
     */
    static labToHex(L, a, b) {
        return this.getDefault().labToHex(L, a, b);
    }
    // ============================================================================
    // DeltaE Color Difference Calculations
    // ============================================================================
    /**
     * Calculate DeltaE (CIE76) between two LAB colors
     * Simple Euclidean distance in LAB space
     * Fast but less perceptually accurate than CIE2000
     * @returns DeltaE value (0 = identical, <1 imperceptible, <3 barely noticeable, >5 clearly different)
     */
    getDeltaE76(lab1, lab2) {
        const dL = lab1.L - lab2.L;
        const da = lab1.a - lab2.a;
        const db = lab1.b - lab2.b;
        return Math.sqrt(dL * dL + da * da + db * db);
    }
    /**
     * Static method: Calculate DeltaE76 using default instance
     */
    static getDeltaE76(lab1, lab2) {
        return this.getDefault().getDeltaE76(lab1, lab2);
    }
    /**
     * Calculate DeltaE (CIEDE2000) between two LAB colors
     * Industry standard for perceptual color difference
     * More accurate but computationally expensive
     * @returns DeltaE value (0 = identical, <1 imperceptible, <3 barely noticeable, >5 clearly different)
     */
    getDeltaE2000(lab1, lab2) {
        const L1 = lab1.L, a1 = lab1.a, b1 = lab1.b;
        const L2 = lab2.L, a2 = lab2.a, b2 = lab2.b;
        // Weighting factors (all set to 1 for standard conditions)
        const kL = 1, kC = 1, kH = 1;
        // Calculate C' and h' for both colors
        const C1 = Math.sqrt(a1 * a1 + b1 * b1);
        const C2 = Math.sqrt(a2 * a2 + b2 * b2);
        const Cab = (C1 + C2) / 2;
        const G = 0.5 * (1 - Math.sqrt(Math.pow(Cab, 7) / (Math.pow(Cab, 7) + Math.pow(25, 7))));
        const a1p = a1 * (1 + G);
        const a2p = a2 * (1 + G);
        const C1p = Math.sqrt(a1p * a1p + b1 * b1);
        const C2p = Math.sqrt(a2p * a2p + b2 * b2);
        const h1p = this.hueAngle(a1p, b1);
        const h2p = this.hueAngle(a2p, b2);
        // Calculate deltas
        const dLp = L2 - L1;
        const dCp = C2p - C1p;
        let dhp;
        if (C1p * C2p === 0) {
            dhp = 0;
        }
        else if (Math.abs(h2p - h1p) <= 180) {
            dhp = h2p - h1p;
        }
        else if (h2p - h1p > 180) {
            dhp = h2p - h1p - 360;
        }
        else {
            dhp = h2p - h1p + 360;
        }
        const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp * Math.PI) / 360);
        // Calculate CIEDE2000 components
        const Lp = (L1 + L2) / 2;
        const Cp = (C1p + C2p) / 2;
        let Hp;
        if (C1p * C2p === 0) {
            Hp = h1p + h2p;
        }
        else if (Math.abs(h1p - h2p) <= 180) {
            Hp = (h1p + h2p) / 2;
        }
        else if (h1p + h2p < 360) {
            Hp = (h1p + h2p + 360) / 2;
        }
        else {
            Hp = (h1p + h2p - 360) / 2;
        }
        const T = 1 -
            0.17 * Math.cos(((Hp - 30) * Math.PI) / 180) +
            0.24 * Math.cos((2 * Hp * Math.PI) / 180) +
            0.32 * Math.cos(((3 * Hp + 6) * Math.PI) / 180) -
            0.2 * Math.cos(((4 * Hp - 63) * Math.PI) / 180);
        const dTheta = 30 * Math.exp(-Math.pow((Hp - 275) / 25, 2));
        const Rc = 2 * Math.sqrt(Math.pow(Cp, 7) / (Math.pow(Cp, 7) + Math.pow(25, 7)));
        const Sl = 1 + (0.015 * Math.pow(Lp - 50, 2)) / Math.sqrt(20 + Math.pow(Lp - 50, 2));
        const Sc = 1 + 0.045 * Cp;
        const Sh = 1 + 0.015 * Cp * T;
        const Rt = -Math.sin((2 * dTheta * Math.PI) / 180) * Rc;
        const dE = Math.sqrt(Math.pow(dLp / (kL * Sl), 2) +
            Math.pow(dCp / (kC * Sc), 2) +
            Math.pow(dHp / (kH * Sh), 2) +
            Rt * (dCp / (kC * Sc)) * (dHp / (kH * Sh)));
        return dE;
    }
    /**
     * Helper to calculate hue angle in degrees from a' and b'
     * @internal
     */
    hueAngle(ap, bp) {
        if (ap === 0 && bp === 0) {
            return 0;
        }
        const h = (Math.atan2(bp, ap) * 180) / Math.PI;
        return h >= 0 ? h : h + 360;
    }
    /**
     * Static method: Calculate DeltaE2000 using default instance
     */
    static getDeltaE2000(lab1, lab2) {
        return this.getDefault().getDeltaE2000(lab1, lab2);
    }
    /**
     * Calculate DeltaE between two hex colors using specified formula
     *
     * Available formulas:
     * - cie76: LAB Euclidean (fast, fair accuracy)
     * - ciede2000: CIEDE2000 (industry standard, accurate); `'cie2000'` is a
     *   legacy alias for it and produces an identical value
     * - oklab: OKLAB Euclidean (modern, simpler than CIEDE2000, CSS standard)
     *
     * @param hex1 First hex color
     * @param hex2 Second hex color
     * @param formula DeltaE formula to use (default: 'cie76')
     * @returns DeltaE value (scale varies by formula)
     */
    getDeltaE(hex1, hex2, formula = 'cie76') {
        switch (normalizeDeltaEFormula(formula)) {
            case 'ciede2000': {
                const lab1 = this.hexToLab(hex1);
                const lab2 = this.hexToLab(hex2);
                return this.getDeltaE2000(lab1, lab2);
            }
            case 'oklab':
                return this.getDeltaE_Oklab(hex1, hex2);
            case 'cie76':
            default: {
                const lab1 = this.hexToLab(hex1);
                const lab2 = this.hexToLab(hex2);
                return this.getDeltaE76(lab1, lab2);
            }
        }
    }
    /**
     * Static method: Calculate DeltaE using default instance
     */
    static getDeltaE(hex1, hex2, formula = 'cie76') {
        return this.getDefault().getDeltaE(hex1, hex2, formula);
    }
    // ============================================================================
    // OKLAB-based Color Difference Calculations
    // ============================================================================
    /**
     * Calculate color difference in OKLAB as **ΔEOK2** — CSS Color 4 §20.4.
     *
     * OKLAB provides better perceptual uniformity than LAB with simpler math
     * than CIEDE2000, and fixes LAB's blue→purple hue shift.
     *
     * ⚠️ `a` and `b` are scaled by 2 before the Euclidean distance. This is not
     * a tweak — it is the metric CSS Color 4 §20.4 defines, and it exists
     * because plain ΔEOK "under-estimates differences in colorfulness compared
     * to differences in lightness". The factor comes from Ottosson's own fits
     * against perceptual datasets (2.016 on COMBVD, 2.045 on OSA-UCS).
     *
     * Measured on this dye set (2,000 random sRGB queries, CIEDE2000 as the
     * reference, asking how often each variant picks a DIFFERENT winning dye):
     *
     *   plain ΔEOK (shipped ≤ 5.0.0)   30.4% disagreement
     *   ΔEOK2 — a,b × 2 (this)         24.4%
     *   cie76, for scale               31.1%
     *
     * §20.5 goes further and recommends ΔEOKr2 (ΔEOK2 plus the toe lightness
     * remap). Measured here it is slightly WORSE (24.3% vs 23.9% in the
     * fact-check's own run) for considerably more code, so it is deliberately
     * not taken.
     *
     * ⚠️ SCALE CHANGED in 5.1.0. Values are roughly 1.4–2× their former size, so
     * any threshold compared against this number had to move with it — see
     * `BAND_VOCABULARY`'s oklab rows and `HARMONY_MAX_DISTANCE.oklab`, both
     * recalibrated in the same change. CSS Color 4 §14.2.1 puts one JND at
     * ΔEOK ≈ 0.02 on the PLAIN scale.
     *
     * Reference: Björn Ottosson (2020) - "A perceptual color space for image processing";
     * CSS Color Module Level 4 §20.4.
     *
     * @param hex1 First color in hex format
     * @param hex2 Second color in hex format
     * @returns Distance value (0 = identical)
     */
    getDeltaE_Oklab(hex1, hex2) {
        const lab1 = this.hexToOklab(hex1);
        const lab2 = this.hexToOklab(hex2);
        const dL = lab2.L - lab1.L;
        const da = (lab2.a - lab1.a) * OKLAB_AB_SCALE;
        const db = (lab2.b - lab1.b) * OKLAB_AB_SCALE;
        return Math.sqrt(dL * dL + da * da + db * db);
    }
    /**
     * Static method: Calculate OKLAB Euclidean distance using default instance
     */
    static getDeltaE_Oklab(hex1, hex2) {
        return this.getDefault().getDeltaE_Oklab(hex1, hex2);
    }
    // ============================================================================
    // OKLAB/OKLCH Color Space Conversion (Björn Ottosson, 2020)
    // ============================================================================
    /**
     * Convert sRGB component to linear RGB
     * Applies inverse gamma companding
     * @internal
     */
    srgbToLinear(c) {
        const normalized = c / 255;
        return normalized <= 0.04045 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
    }
    /**
     * Convert linear RGB component to sRGB
     * Applies gamma companding
     * @internal
     */
    linearToSrgb(c) {
        const clamped = clamp(c, 0, 1);
        const srgb = clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
        return Math.round(srgb * 255);
    }
    /**
     * Convert RGB to OKLAB color space
     *
     * OKLAB is a modern perceptually uniform color space that fixes issues
     * with CIELAB, particularly for blue colors. Blue + Yellow = Green in OKLAB.
     *
     * @param r Red component (0-255)
     * @param g Green component (0-255)
     * @param b Blue component (0-255)
     * @returns OKLAB color with L (0-1), a (~-0.4 to 0.4), b (~-0.4 to 0.4)
     *
     * @example rgbToOklab(255, 0, 0) -> { L: 0.628, a: 0.225, b: 0.126 }
     */
    rgbToOklab(r, g, b) {
        if (!isValidRGB(r, g, b)) {
            throw new AppError(ErrorCode.INVALID_RGB_VALUE, `Invalid RGB values: r=${r}, g=${g}, b=${b}. Values must be 0-255`, 'error');
        }
        // OPT-001: LRU cache for OKLAB conversions (hot path for dye matching)
        const cacheKey = `${r},${g},${b}`;
        const cached = this.rgbToOklabCache.get(cacheKey);
        if (cached) {
            // BUG-005 (2026-07-18 audit): defensive copy — callers mutating the
            // returned object must not poison the process-wide cache
            return { ...cached };
        }
        // Convert sRGB to linear RGB
        const rLin = this.srgbToLinear(r);
        const gLin = this.srgbToLinear(g);
        const bLin = this.srgbToLinear(b);
        const oklab = this.linearRgbToOklab(rLin, gLin, bLin);
        const result = {
            L: round(oklab.L, 6),
            a: round(oklab.a, 6),
            b: round(oklab.b, 6),
        };
        this.rgbToOklabCache.set(cacheKey, result);
        return { ...result };
    }
    /**
     * Linear sRGB (0–1, unclamped) → OKLAB. The matrix half of `rgbToOklab`,
     * shared with the gamut mapper, which must evaluate out-of-gamut colours.
     * @internal
     */
    linearRgbToOklab(rLin, gLin, bLin) {
        const l = Math.cbrt(0.4122214708 * rLin + 0.5363325363 * gLin + 0.0514459929 * bLin);
        const m = Math.cbrt(0.2119034982 * rLin + 0.6806995451 * gLin + 0.1073969566 * bLin);
        const s = Math.cbrt(0.0883024619 * rLin + 0.2817188376 * gLin + 0.6299787005 * bLin);
        return {
            L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
            a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
            b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
        };
    }
    /**
     * Static method: Convert RGB to OKLAB using default instance
     */
    static rgbToOklab(r, g, b) {
        return this.getDefault().rgbToOklab(r, g, b);
    }
    /**
     * Convert OKLAB to RGB color space
     *
     * @param L Lightness (0-1)
     * @param a Green-Red axis (~-0.4 to 0.4)
     * @param b Blue-Yellow axis (~-0.4 to 0.4)
     * @returns RGB color with values 0-255
     *
     * @example oklabToRgb(0.628, 0.225, 0.126) -> { r: 255, g: 0, b: 0 }
     */
    oklabToRgb(L, a, b) {
        const lin = this.oklabToLinearRgb(L, a, b);
        return {
            r: clamp(this.linearToSrgb(lin.r), RGB_MIN, RGB_MAX),
            g: clamp(this.linearToSrgb(lin.g), RGB_MIN, RGB_MAX),
            b: clamp(this.linearToSrgb(lin.b), RGB_MIN, RGB_MAX),
        };
    }
    /**
     * OKLAB → linear sRGB (0–1, UNCLAMPED). The matrix half of `oklabToRgb`;
     * a value outside 0–1 means "outside the sRGB gamut".
     * @internal
     */
    oklabToLinearRgb(L, a, b) {
        const lRoot = L + 0.3963377774 * a + 0.2158037573 * b;
        const mRoot = L - 0.1055613458 * a - 0.0638541728 * b;
        const sRoot = L - 0.0894841775 * a - 1.291485548 * b;
        const l = lRoot * lRoot * lRoot;
        const m = mRoot * mRoot * mRoot;
        const s = sRoot * sRoot * sRoot;
        return {
            r: +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
            g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
            b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
        };
    }
    /**
     * Static method: Convert OKLAB to RGB using default instance
     */
    static oklabToRgb(L, a, b) {
        return this.getDefault().oklabToRgb(L, a, b);
    }
    /**
     * Convert hex color to OKLAB
     */
    hexToOklab(hex) {
        const rgb = this.hexToRgb(hex);
        return this.rgbToOklab(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert hex to OKLAB using default instance
     */
    static hexToOklab(hex) {
        return this.getDefault().hexToOklab(hex);
    }
    /**
     * Convert OKLAB to hex color
     */
    oklabToHex(L, a, b) {
        const rgb = this.oklabToRgb(L, a, b);
        return this.rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert OKLAB to hex using default instance
     */
    static oklabToHex(L, a, b) {
        return this.getDefault().oklabToHex(L, a, b);
    }
    /**
     * Convert RGB to OKLCH (cylindrical OKLAB)
     *
     * OKLCH expresses OKLAB in cylindrical coordinates for intuitive
     * hue manipulation. Ideal for gradient interpolation.
     *
     * @param r Red component (0-255)
     * @param g Green component (0-255)
     * @param b Blue component (0-255)
     * @returns OKLCH color with L (0-1), C (chroma, 0 to ~0.4), h (hue, 0-360)
     *
     * @example rgbToOklch(255, 0, 0) -> { L: 0.628, C: 0.258, h: 29.23 }
     */
    rgbToOklch(r, g, b) {
        const oklab = this.rgbToOklab(r, g, b);
        // Convert to cylindrical coordinates
        const C = Math.sqrt(oklab.a * oklab.a + oklab.b * oklab.b);
        let h = Math.atan2(oklab.b, oklab.a) * (180 / Math.PI);
        if (h < 0)
            h += 360;
        return {
            L: oklab.L,
            C: round(C, 6),
            h: round(h, 4),
        };
    }
    /**
     * Static method: Convert RGB to OKLCH using default instance
     */
    static rgbToOklch(r, g, b) {
        return this.getDefault().rgbToOklch(r, g, b);
    }
    /**
     * Convert OKLCH to RGB
     *
     * @param L Lightness (0-1)
     * @param C Chroma (0 to ~0.4)
     * @param h Hue angle (0-360 degrees)
     * @returns RGB color with values 0-255
     */
    oklchToRgb(L, C, h) {
        // Convert from cylindrical to rectangular coordinates
        const hRad = h * (Math.PI / 180);
        const a = C * Math.cos(hRad);
        const b = C * Math.sin(hRad);
        return this.oklabToRgb(L, a, b);
    }
    /**
     * Static method: Convert OKLCH to RGB using default instance
     */
    static oklchToRgb(L, C, h) {
        return this.getDefault().oklchToRgb(L, C, h);
    }
    /**
     * Convert hex color to OKLCH
     */
    hexToOklch(hex) {
        const rgb = this.hexToRgb(hex);
        return this.rgbToOklch(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert hex to OKLCH using default instance
     */
    static hexToOklch(hex) {
        return this.getDefault().hexToOklch(hex);
    }
    /**
     * Convert OKLCH to hex color
     */
    oklchToHex(L, C, h) {
        const rgb = this.oklchToRgb(L, C, h);
        return this.rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert OKLCH to hex using default instance
     */
    static oklchToHex(L, C, h) {
        return this.getDefault().oklchToHex(L, C, h);
    }
    // ============================================================================
    // Gamut mapping (CSS Color 4 §14 — binary search with local MINDE)
    // ============================================================================
    static GAMUT_JND = 0.02;
    static GAMUT_EPSILON = 0.0001;
    /** Whether an unclamped linear-sRGB triple lies inside the display gamut. */
    inSrgbGamut(lin) {
        const t = 1e-6;
        return lin.r >= -t && lin.r <= 1 + t && lin.g >= -t && lin.g <= 1 + t && lin.b >= -t && lin.b <= 1 + t;
    }
    /** Plain ΔEOK (Euclidean in OKLAB), the metric CSS Color 4 uses for its JND. Not ΔEOK2. */
    deltaEOkPlain(a, b) {
        return Math.hypot(a.L - b.L, a.a - b.a, a.b - b.b);
    }
    /** Clamp a linear triple into 0–1 and encode as hex. */
    linearToHexClipped(lin) {
        return this.rgbToHex(clamp(this.linearToSrgb(lin.r), RGB_MIN, RGB_MAX), clamp(this.linearToSrgb(lin.g), RGB_MIN, RGB_MAX), clamp(this.linearToSrgb(lin.b), RGB_MIN, RGB_MAX));
    }
    /**
     * Bring an OKLCH colour into sRGB the way CSS Color 4 §14 specifies: keep L
     * and h, bisect C, and accept a per-channel clip as soon as it is within one
     * just-noticeable difference (ΔEOK 0.02) of the candidate.
     *
     * `oklchToHex` clips per channel instead, which changes HUE — the OKLCH
     * complement of #0000FF comes out #A02000 (dark red) under clipping and
     * #734F00 (dark olive) here (research 02 Table B). Every drawn or matched
     * colour produced by hue rotation in OKLCH must come through this method.
     *
     * @param L Lightness 0–1  @param C Chroma ≥ 0  @param h Hue 0–360
     */
    gamutMapOklch(L, C, h) {
        if (L >= 1)
            return '#FFFFFF';
        if (L <= 0)
            return '#000000';
        const JND = ColorConverter.GAMUT_JND;
        const EPS = ColorConverter.GAMUT_EPSILON;
        const hRad = h * (Math.PI / 180);
        const labAt = (c) => ({ L, a: c * Math.cos(hRad), b: c * Math.sin(hRad) });
        const clipLin = (lin) => ({
            r: clamp(lin.r, 0, 1),
            g: clamp(lin.g, 0, 1),
            b: clamp(lin.b, 0, 1),
        });
        let lab = labAt(C);
        let current = this.oklabToLinearRgb(lab.L, lab.a, lab.b);
        if (this.inSrgbGamut(current))
            return this.linearToHexClipped(current);
        let clipped = clipLin(current);
        if (this.deltaEOkPlain(this.linearRgbToOklab(clipped.r, clipped.g, clipped.b), lab) < JND) {
            return this.linearToHexClipped(clipped);
        }
        let min = 0;
        let max = C;
        let minInGamut = true;
        while (max - min > EPS) {
            const chroma = (min + max) / 2;
            lab = labAt(chroma);
            current = this.oklabToLinearRgb(lab.L, lab.a, lab.b);
            if (minInGamut && this.inSrgbGamut(current)) {
                min = chroma;
                continue;
            }
            clipped = clipLin(current);
            const E = this.deltaEOkPlain(this.linearRgbToOklab(clipped.r, clipped.g, clipped.b), lab);
            if (E < JND) {
                if (JND - E < EPS)
                    return this.linearToHexClipped(clipped);
                minInGamut = false;
                min = chroma;
            }
            else {
                max = chroma;
            }
        }
        return this.linearToHexClipped(clipped);
    }
    /** Static: see the instance method. */
    static gamutMapOklch(L, C, h) {
        return this.getDefault().gamutMapOklch(L, C, h);
    }
    /**
     * The largest C such that every chroma in [0, C] is inside sRGB along
     * (L, h); the sRGB solid is not connected along the blue ray (h ≈ 264°), so
     * the true outer extent can be larger.
     *
     * Used to paint a perceptual ring that is in gamut at every angle, which is
     * why "connected from zero" is the property that matters: a chroma reached
     * across a gamut hole is a colour the mapper immediately pulls back
     * somewhere else. A plain lo/hi bisection cannot answer this — it samples
     * the midpoint first and, at L ≈ 0.11 / h ≈ 264°, lands in the far lobe that
     * makes #0000FF legal and reports a chroma with a hole beneath it. So: a
     * coarse upward scan to find the FIRST exit, then bisect inside that step.
     */
    maxChromaOklch(L, h) {
        if (L <= 0 || L >= 1)
            return 0;
        const hRad = h * (Math.PI / 180);
        const cos = Math.cos(hRad);
        const sin = Math.sin(hRad);
        const inside = (c) => this.inSrgbGamut(this.oklabToLinearRgb(L, c * cos, c * sin));
        // sRGB's largest OKLab chroma is ≈ 0.32 (pure magenta); 0.4 clears it.
        const STEP = 0.005;
        const STEPS = 80;
        let lo = 0;
        let hi = -1;
        for (let i = 1; i <= STEPS; i++) {
            const c = i * STEP;
            if (inside(c)) {
                lo = c;
            }
            else {
                hi = c;
                break;
            }
        }
        if (hi < 0)
            return lo; // in gamut all the way out — no exit to bisect
        for (let i = 0; i < 24; i++) {
            const mid = (lo + hi) / 2;
            if (inside(mid))
                lo = mid;
            else
                hi = mid;
        }
        return lo;
    }
    /** Static: see the instance method. */
    static maxChromaOklch(L, h) {
        return this.getDefault().maxChromaOklch(L, h);
    }
    // ============================================================================
    // LCH Color Space Conversion (Cylindrical CIE LAB)
    // ============================================================================
    /**
     * Convert CIE LAB to LCH (cylindrical LAB)
     *
     * LCH expresses LAB in cylindrical coordinates for hue-based operations.
     *
     * @param L Lightness (0-100)
     * @param a Green-Red axis (~-128 to 127)
     * @param b Blue-Yellow axis (~-128 to 127)
     * @returns LCH color with L (0-100), C (chroma), h (hue, 0-360)
     */
    labToLch(L, a, b) {
        const C = Math.sqrt(a * a + b * b);
        let h = Math.atan2(b, a) * (180 / Math.PI);
        if (h < 0)
            h += 360;
        return {
            L: round(L, 4),
            C: round(C, 4),
            h: round(h, 4),
        };
    }
    /**
     * Static method: Convert LAB to LCH using default instance
     */
    static labToLch(L, a, b) {
        return this.getDefault().labToLch(L, a, b);
    }
    /**
     * Convert LCH to CIE LAB
     *
     * @param L Lightness (0-100)
     * @param C Chroma
     * @param h Hue angle (0-360 degrees)
     * @returns LAB color
     */
    lchToLab(L, C, h) {
        const hRad = h * (Math.PI / 180);
        return {
            L: round(L, 4),
            a: round(C * Math.cos(hRad), 4),
            b: round(C * Math.sin(hRad), 4),
        };
    }
    /**
     * Static method: Convert LCH to LAB using default instance
     */
    static lchToLab(L, C, h) {
        return this.getDefault().lchToLab(L, C, h);
    }
    /**
     * Convert RGB to LCH
     */
    rgbToLch(r, g, b) {
        const lab = this.rgbToLab(r, g, b);
        return this.labToLch(lab.L, lab.a, lab.b);
    }
    /**
     * Static method: Convert RGB to LCH using default instance
     */
    static rgbToLch(r, g, b) {
        return this.getDefault().rgbToLch(r, g, b);
    }
    /**
     * Convert LCH to RGB
     */
    lchToRgb(L, C, h) {
        const lab = this.lchToLab(L, C, h);
        return this.labToRgb(lab.L, lab.a, lab.b);
    }
    /**
     * Static method: Convert LCH to RGB using default instance
     */
    static lchToRgb(L, C, h) {
        return this.getDefault().lchToRgb(L, C, h);
    }
    /**
     * Convert hex color to LCH
     */
    hexToLch(hex) {
        const rgb = this.hexToRgb(hex);
        return this.rgbToLch(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert hex to LCH using default instance
     */
    static hexToLch(hex) {
        return this.getDefault().hexToLch(hex);
    }
    /**
     * Convert LCH to hex color
     */
    lchToHex(L, C, h) {
        const rgb = this.lchToRgb(L, C, h);
        return this.rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert LCH to hex using default instance
     */
    static lchToHex(L, C, h) {
        return this.getDefault().lchToHex(L, C, h);
    }
    // ============================================================================
    // HSL Color Space Conversion
    // ============================================================================
    /**
     * Convert RGB to HSL (Hue, Saturation, Lightness)
     *
     * HSL is similar to HSV but uses Lightness instead of Value.
     * Common in design tools like Photoshop, Figma, and CSS.
     *
     * @param r Red component (0-255)
     * @param g Green component (0-255)
     * @param b Blue component (0-255)
     * @returns HSL color with h (0-360), s (0-100), l (0-100)
     *
     * @example rgbToHsl(255, 0, 0) -> { h: 0, s: 100, l: 50 }
     */
    rgbToHsl(r, g, b) {
        if (!isValidRGB(r, g, b)) {
            throw new AppError(ErrorCode.INVALID_RGB_VALUE, `Invalid RGB values: r=${r}, g=${g}, b=${b}. Values must be 0-255`, 'error');
        }
        // Normalize to 0-1
        const rNorm = r / 255;
        const gNorm = g / 255;
        const bNorm = b / 255;
        const max = Math.max(rNorm, gNorm, bNorm);
        const min = Math.min(rNorm, gNorm, bNorm);
        const delta = max - min;
        // Calculate Lightness
        const l = (max + min) / 2;
        // Calculate Saturation
        let s = 0;
        if (delta !== 0) {
            s = delta / (1 - Math.abs(2 * l - 1));
        }
        // Calculate Hue (same as HSV)
        let h = 0;
        if (delta !== 0) {
            if (max === rNorm) {
                h = ((gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0)) * 60;
            }
            else if (max === gNorm) {
                h = ((bNorm - rNorm) / delta + 2) * 60;
            }
            else {
                h = ((rNorm - gNorm) / delta + 4) * 60;
            }
        }
        return {
            h: round(h, 2),
            s: round(s * 100, 2),
            l: round(l * 100, 2),
        };
    }
    /**
     * Static method: Convert RGB to HSL using default instance
     */
    static rgbToHsl(r, g, b) {
        return this.getDefault().rgbToHsl(r, g, b);
    }
    /**
     * Convert HSL to RGB
     *
     * @param h Hue (0-360 degrees)
     * @param s Saturation (0-100 percent)
     * @param l Lightness (0-100 percent)
     * @returns RGB color with values 0-255
     *
     * @example hslToRgb(0, 100, 50) -> { r: 255, g: 0, b: 0 }
     */
    hslToRgb(h, s, l) {
        // Normalize
        const hNorm = this.normalizeHue(h) / 360;
        const sNorm = clamp(s, 0, 100) / 100;
        const lNorm = clamp(l, 0, 100) / 100;
        let r, g, b;
        if (sNorm === 0) {
            // Achromatic (gray)
            r = g = b = lNorm;
        }
        else {
            const q = lNorm < 0.5 ? lNorm * (1 + sNorm) : lNorm + sNorm - lNorm * sNorm;
            const p = 2 * lNorm - q;
            r = this.hueToRgbComponent(p, q, hNorm + 1 / 3);
            g = this.hueToRgbComponent(p, q, hNorm);
            b = this.hueToRgbComponent(p, q, hNorm - 1 / 3);
        }
        return {
            r: clamp(Math.round(r * 255), RGB_MIN, RGB_MAX),
            g: clamp(Math.round(g * 255), RGB_MIN, RGB_MAX),
            b: clamp(Math.round(b * 255), RGB_MIN, RGB_MAX),
        };
    }
    /**
     * Helper for HSL to RGB conversion
     * @internal
     */
    hueToRgbComponent(p, q, t) {
        if (t < 0)
            t += 1;
        if (t > 1)
            t -= 1;
        if (t < 1 / 6)
            return p + (q - p) * 6 * t;
        if (t < 1 / 2)
            return q;
        if (t < 2 / 3)
            return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    }
    /**
     * Static method: Convert HSL to RGB using default instance
     */
    static hslToRgb(h, s, l) {
        return this.getDefault().hslToRgb(h, s, l);
    }
    /**
     * Convert hex color to HSL
     */
    hexToHsl(hex) {
        const rgb = this.hexToRgb(hex);
        return this.rgbToHsl(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert hex to HSL using default instance
     */
    static hexToHsl(hex) {
        return this.getDefault().hexToHsl(hex);
    }
    /**
     * Convert HSL to hex color
     */
    hslToHex(h, s, l) {
        const rgb = this.hslToRgb(h, s, l);
        return this.rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert HSL to hex using default instance
     */
    static hslToHex(h, s, l) {
        return this.getDefault().hslToHex(h, s, l);
    }
    /**
     * Convert RGB to CMYK (Cyan, Magenta, Yellow, Key/Black)
     *
     * Naive device-independent conversion (no ICC profile) — suitable for
     * display/reference values shown alongside HEX/RGB/HSV, not for print
     * production.
     *
     * @param r Red component (0-255)
     * @param g Green component (0-255)
     * @param b Blue component (0-255)
     * @returns CMYK color with c/m/y/k as 0-100 percentages
     *
     * @example rgbToCmyk(255, 0, 0) -> { c: 0, m: 100, y: 100, k: 0 }
     */
    rgbToCmyk(r, g, b) {
        if (!isValidRGB(r, g, b)) {
            throw new AppError(ErrorCode.INVALID_RGB_VALUE, `Invalid RGB values: r=${r}, g=${g}, b=${b}. Values must be 0-255`, 'error');
        }
        const rNorm = r / 255;
        const gNorm = g / 255;
        const bNorm = b / 255;
        const k = 1 - Math.max(rNorm, gNorm, bNorm);
        // Pure black: avoid division by zero
        if (k === 1) {
            return { c: 0, m: 0, y: 0, k: 100 };
        }
        const c = (1 - rNorm - k) / (1 - k);
        const m = (1 - gNorm - k) / (1 - k);
        const y = (1 - bNorm - k) / (1 - k);
        return {
            c: round(c * 100, 2),
            m: round(m * 100, 2),
            y: round(y * 100, 2),
            k: round(k * 100, 2),
        };
    }
    /**
     * Static method: Convert RGB to CMYK using default instance
     */
    static rgbToCmyk(r, g, b) {
        return this.getDefault().rgbToCmyk(r, g, b);
    }
    /**
     * Convert CMYK to RGB
     *
     * @param c Cyan (0-100 percent)
     * @param m Magenta (0-100 percent)
     * @param y Yellow (0-100 percent)
     * @param k Key/Black (0-100 percent)
     * @returns RGB color with values 0-255
     *
     * @example cmykToRgb(0, 100, 100, 0) -> { r: 255, g: 0, b: 0 }
     */
    cmykToRgb(c, m, y, k) {
        const cNorm = clamp(c, 0, 100) / 100;
        const mNorm = clamp(m, 0, 100) / 100;
        const yNorm = clamp(y, 0, 100) / 100;
        const kNorm = clamp(k, 0, 100) / 100;
        return {
            r: clamp(Math.round(255 * (1 - cNorm) * (1 - kNorm)), RGB_MIN, RGB_MAX),
            g: clamp(Math.round(255 * (1 - mNorm) * (1 - kNorm)), RGB_MIN, RGB_MAX),
            b: clamp(Math.round(255 * (1 - yNorm) * (1 - kNorm)), RGB_MIN, RGB_MAX),
        };
    }
    /**
     * Static method: Convert CMYK to RGB using default instance
     */
    static cmykToRgb(c, m, y, k) {
        return this.getDefault().cmykToRgb(c, m, y, k);
    }
    /**
     * Convert hex color to CMYK
     */
    hexToCmyk(hex) {
        const rgb = this.hexToRgb(hex);
        return this.rgbToCmyk(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert hex to CMYK using default instance
     */
    static hexToCmyk(hex) {
        return this.getDefault().hexToCmyk(hex);
    }
    /**
     * Convert CMYK to hex color
     */
    cmykToHex(c, m, y, k) {
        const rgb = this.cmykToRgb(c, m, y, k);
        return this.rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    /**
     * Static method: Convert CMYK to hex using default instance
     */
    static cmykToHex(c, m, y, k) {
        return this.getDefault().cmykToHex(c, m, y, k);
    }
}
//# sourceMappingURL=ColorConverter.js.map